<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { VServerInfo } from '@/types/dashboard/vserver/VServerInfo'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const server = ref<VServerInfo | null>(null)
const loading = ref(true)
const busy = ref(false)

const id = computed(() => Number(route.params.id))

async function fetchServer() {
  loading.value = true
  try {
    const res = await useAxios<null, VServerInfo>(
      'GET',
      `/admin/vserver/servers/${id.value}`,
      true
    )
    if (res?.status === STATUS_CODES.OK) {
      const parsed = VServerInfo.safeParse(res.data)
      if (parsed.success) server.value = parsed.data
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerDetail' })
  } finally {
    loading.value = false
  }
}

async function forceAction(action: 'provision' | 'deprovision') {
  if (!window.confirm(t(`dashboard.vserver.admin.detail.force${action === 'provision' ? 'Provision' : 'Deprovision'}`))) return
  busy.value = true
  try {
    await useAxios('POST', `/admin/vserver/servers/${id.value}/${action}`, true)
    await fetchServer()
  } catch (err) {
    handleAxiosError(err, { logPrefix: `AdminVServerDetail:${action}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getErrorMessage(err)
    })
  } finally {
    busy.value = false
  }
}

onMounted(fetchServer)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.detail.title')"
    :subtitle="server?.hostname ?? ''"
    icon="mdi-server"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <template v-else-if="server">
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.vserver.overview.sectionTitle') }}</h3>
        </div>
        <div class="kv">
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.hostname') }}</span>
            <span class="mono">{{ server.hostname }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.status') }}</span>
            <span>{{ $t(`dashboard.vserver.status.${server.status}`) }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.ipv4') }}</span>
            <span class="mono">{{ server.ipv4_address ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.ipv6') }}</span>
            <span class="mono">{{ server.ipv6_address ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.plan') }}</span>
            <span>{{ server.plan_slug }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.proxmoxVmid') }}</span>
            <span class="mono">{{ server.proxmox_vmid ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.proxmoxNode') }}</span>
            <span class="mono">{{ server.proxmox_node ?? '-' }}</span>
          </div>
          <div v-if="server.error_message" class="kv-row error">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.errorLabel') }}</span>
            <span>{{ server.error_message }}</span>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.vserver.admin.detail.title') }}</h3>
        </div>
        <div class="actions-row">
          <button
            type="button"
            class="btn-text"
            :disabled="busy"
            @click="forceAction('provision')"
          >
            <v-icon icon="mdi-cog-play" size="18" />
            {{ $t('dashboard.vserver.admin.detail.forceProvision') }}
          </button>
          <button
            type="button"
            class="btn-text btn-danger"
            :disabled="busy"
            @click="forceAction('deprovision')"
          >
            <v-icon icon="mdi-delete-sweep" size="18" />
            {{ $t('dashboard.vserver.admin.detail.forceDeprovision') }}
          </button>
          <button type="button" class="btn-text" @click="router.push({ name: 'dashboardAdminVServer' })">
            <v-icon icon="mdi-arrow-left" size="18" />
            {{ $t('dashboard.vserver.admin.detail.backToList') }}
          </button>
        </div>
      </div>
    </template>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.kv {
  display: flex;
  flex-direction: column;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &.error {
    color: #ef5350;
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.9);
}

.actions-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.82rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }
}

.btn-danger {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.25);
  color: #ef5350;
}

.state-box {
  display: flex;
  justify-content: center;
  padding: 48px;
}
</style>
